/**
 * Cores das categorias: cada uma diferente das outras ao olho (distância no espaço OKLab, que segue a percepção) e
 * legível nos dois temas (claridade média e saturação moderada). A sugestão automática escolhe, entre cores de
 * claridade e saturação fixas em toda a volta do círculo, a mais distante das que já estão em uso.
 */

const clamp01 = (x: number) => Math.min(1, Math.max(0, x));
const toLinear = (c: number) => (c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
const toGamma = (c: number) => (c <= 0.0031308 ? 12.92 * c : 1.055 * c ** (1 / 2.4) - 0.055);

export function isHexColor(s: unknown): s is string {
  return typeof s === 'string' && /^#[0-9a-f]{6}$/i.test(s);
}

/** #RRGGBB → OKLab [L, a, b]. */
export function hexToOklab(hex: string): [number, number, number] {
  const n = parseInt(hex.slice(1), 16);
  const [r, g, b] = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map((v) => toLinear(v / 255)) as [number, number, number];
  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
  const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);
  return [0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s, 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s, 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s];
}

function oklabToRgb(L: number, a: number, b: number): [number, number, number] {
  const l = (L + 0.3963377774 * a + 0.2158037573 * b) ** 3;
  const m = (L - 0.1055613458 * a - 0.0638541728 * b) ** 3;
  const s = (L - 0.0894841775 * a - 1.291485548 * b) ** 3;
  return [4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s, -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s, -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s];
}

/** OKLCh → #RRGGBB, reduzindo a saturação até caber nas cores da tela. */
export function oklchToHex(L: number, C: number, hDeg: number): string {
  const h = (hDeg * Math.PI) / 180;
  let c = C;
  let rgb = oklabToRgb(L, c * Math.cos(h), c * Math.sin(h));
  while (rgb.some((v) => v < -0.0005 || v > 1.0005) && c > 0) {
    c = Math.max(0, c - 0.005);
    rgb = oklabToRgb(L, c * Math.cos(h), c * Math.sin(h));
  }
  return `#${rgb.map((v) => Math.round(clamp01(toGamma(clamp01(v))) * 255).toString(16).padStart(2, '0')).join('')}`.toUpperCase();
}

/** Diferença entre duas cores (OKLab × 100). Abaixo de MIN_COLOR_DISTANCE, ao lado uma da outra, parecem a mesma. */
export function colorDistance(x: string, y: string): number {
  const [l1, a1, b1] = hexToOklab(x);
  const [l2, a2, b2] = hexToOklab(y);
  return Math.hypot(l1 - l2, a1 - a2, b1 - b2) * 100;
}
export const MIN_COLOR_DISTANCE = 12;

/** Cor legível nos dois temas: nem clara nem escura demais (o ponto da categoria aparece no fundo claro e no escuro). */
export function readableColor(hex: string): boolean {
  const L = hexToOklab(hex)[0];
  return L >= 0.5 && L <= 0.8;
}

/** A menor distância entre a cor e as já usadas (Infinity sem nenhuma). */
export function minDistance(hex: string, taken: readonly string[]): number {
  return taken.filter(isHexColor).reduce((d, t) => Math.min(d, colorDistance(hex, t)), Infinity);
}

const CANDIDATES: string[] = (() => {
  const out: string[] = [];
  for (const L of [0.66, 0.56, 0.76]) for (let h = 0; h < 360; h += 5) out.push(oklchToHex(L, 0.16, h));
  return [...new Set(out)];
})();

/** A cor mais distante das já usadas (com várias empatadas, a de claridade média, a primeira da lista). */
export function suggestColor(taken: readonly string[]): string {
  let best = CANDIDATES[0]!;
  let bestD = -1;
  for (const c of CANDIDATES) {
    const d = minDistance(c, taken);
    if (d > bestD + 0.01) { best = c; bestD = d; }
  }
  return best;
}

/**
 * Cores para uma lista em ordem de prioridade (a mais usada primeiro): mantém a cor de quem já tem uma legível e
 * distante das mantidas antes; as outras ganham a sugestão. Devolve só o que muda.
 */
export function distributeColors<T extends { id: number; color: string | null }>(items: readonly T[]): { id: number; from: string | null; to: string }[] {
  const kept: string[] = [];
  const changes: { id: number; from: string | null; to: string }[] = [];
  for (const it of items) {
    // Mantém a cor atual se ela é quase tão boa quanto a melhor sugestão possível (com muitas categorias, o círculo
    // de cores não dá MIN_COLOR_DISTANCE para todas: vale o mais distante que couber).
    const bar = Math.min(MIN_COLOR_DISTANCE, 0.8 * minDistance(suggestColor(kept), kept));
    if (isHexColor(it.color) && readableColor(it.color) && minDistance(it.color, kept) >= bar) {
      kept.push(it.color);
      continue;
    }
    const to = suggestColor(kept);
    kept.push(to);
    changes.push({ id: it.id, from: it.color, to });
  }
  return changes;
}
